import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { PriceEventTrackingProvider } from '@udemy-v2/react-currency-formatting';
interface WithPriceEventTrackingContextOptions {
  context: string;
}

/**
 * Higher-order component that wraps a component with PriceEventTrackingProvider.
 * This ensures all pricing events triggered within the wrapped component have the specified context.
 *
 * @param Component - The component to wrap
 * @param options - Configuration options including the context value
 * @returns A wrapped component with price event tracking context
 *
 * @example
 * ```tsx
 * const ComponentWithTracking = withPriceEventTrackingContext(MyComponent, {
 *   context: 'cartSuccessModal'
 * });
 * ```
 */
export function withPriceEventTrackingContext<TProps extends object = Record<string, unknown>>(Component: React.ComponentType<TProps>, options: WithPriceEventTrackingContextOptions): React.ComponentType<TProps> {
  const WrappedComponent = props => {
    const $ = _c(3);
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        context: options.context
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] !== props) {
      t1 = <PriceEventTrackingProvider priceEventTrackingContext={t0}><Component {...props} /></PriceEventTrackingProvider>;
      $[1] = props;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  };
  const componentName = Component.displayName || Component.name || 'Component';
  WrappedComponent.displayName = `withPriceEventTrackingContext(${componentName})`;
  return WrappedComponent;
}